import { utils } from '@app/shared';
import { TicketLog, TicketTask } from '../models/ticket.model';

/** Id do usuário logado no contexto do company group (mesma regra usada na barra de ações). */
export function getCurrentUserId(): string | undefined {
  const user = utils.getUser();
  return utils.getBwUserCompanyGroup(user)?.user_id || user?.id;
}

/**
 * Retorna o log de apontamento em andamento vinculado à tarefa.
 *
 * O vínculo é feito exclusivamente por id (`task_id` / `task.id`), do mesmo jeito que o quadro de
 * sprint faz. Qualquer fallback por descrição faz tarefas com o mesmo texto compartilharem o mesmo
 * log ativo e exibirem a badge "apontando" duplicada.
 */
export function findActiveTaskLog(
  logs: TicketLog[] | undefined | null,
  task: Pick<TicketTask, 'id'> | null | undefined
): TicketLog | null {
  if (!logs?.length || !task?.id) return null;

  const taskId = String(task.id);

  return (
    logs.find(log => {
      if (!log.start_date || log.end_date) return false;
      return getLogTaskId(log) === taskId;
    }) ?? null
  );
}

/**
 * Apontamento em andamento do usuário informado **neste chamado** — vinculado a uma tarefa ou ao
 * chamado como um todo.
 */
export function findUserActiveTicketLog(
  logs: TicketLog[] | undefined | null,
  userId: string | undefined | null
): TicketLog | null {
  if (!logs?.length || !userId) return null;

  return (
    logs.find(log => {
      if (!log.start_date || log.end_date) return false;
      return log.user_id === userId || log.user?.id === userId;
    }) ?? null
  );
}

/**
 * true quando o usuário já tem apontamento ativo neste chamado vinculado a OUTRA tarefa (ou ao
 * próprio chamado, sem tarefa).
 *
 * Só é permitido um apontamento ativo por chamado: nesse caso o modal de apontamento abre em modo
 * "encerrar", com a tarefa do apontamento ativo pré-selecionada — clicar no play de outra tarefa
 * não faz o que o usuário espera. A restrição é por chamado; apontamento em outro chamado não
 * bloqueia nada aqui.
 */
export function isTaskTimeEntryBlocked(
  logs: TicketLog[] | undefined | null,
  task: Pick<TicketTask, 'id'> | null | undefined,
  userId: string | undefined | null
): boolean {
  const activeLog = findUserActiveTicketLog(logs, userId);
  if (!activeLog || !task?.id) return false;

  return getLogTaskId(activeLog) !== String(task.id);
}

function getLogTaskId(log: TicketLog): string | null {
  const linkedTaskId = log.task_id ?? log.task?.id;
  return linkedTaskId === null || linkedTaskId === undefined ? null : String(linkedTaskId);
}
